import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Memory Matrix — vue React staircase + debrief.
 *
 * Stage : grille N x N (fixe 280x280) qui flashe k cellules simultanement,
 * puis l'utilisateur tape k cellules pour reproduire le pattern.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig, variantForLevel } from './params';
const STAGE_SIZE = 280;
function MatrixGrid({ side, litCells, pickedCells, truthCells, feedback, showFeedback, onTap, disabled, }) {
    const { t } = useTranslation('games');
    const total = side * side;
    const cells = [];
    for (let i = 0; i < total; i += 1)
        cells.push(i);
    return (_jsx("div", { className: "grid gap-s2 border border-cyan-border-faint bg-cyan-surface p-s3", style: {
            gridTemplateColumns: `repeat(${side}, minmax(0, 1fr))`,
            width: STAGE_SIZE,
            height: STAGE_SIZE,
        }, children: cells.map((idx) => {
            const isLit = litCells.has(idx);
            const isPicked = pickedCells.has(idx);
            const isTruth = truthCells.has(idx);
            let stateClass = 'bg-cyan-bg text-cyan-text-secondary';
            if (showFeedback) {
                if (feedback === 'correct' && isPicked) {
                    stateClass = 'bg-cyan-core text-cyan-surface';
                }
                else if (feedback === 'incorrect') {
                    if (isPicked && !isTruth)
                        stateClass = 'bg-cyan-media text-cyan-surface';
                    else if (isPicked && isTruth)
                        stateClass = 'bg-cyan-core text-cyan-surface';
                    else if (!isPicked && isTruth)
                        stateClass = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
                }
            }
            else if (isLit) {
                stateClass = 'bg-cyan-network text-cyan-surface scale-95';
            }
            else if (isPicked) {
                stateClass = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
            }
            return (_jsx("button", { type: "button", onClick: () => onTap && !disabled && onTap(idx), disabled: disabled, "aria-label": t('memory_matrix.cell_aria', { idx: idx + 1 }), className: [
                    'flex aspect-square items-center justify-center',
                    'transition-all duration-feedback ease-soft-wind',
                    stateClass,
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer active:scale-95',
                ].join(' ') }, idx));
        }) }));
}
export function MMStaircaseView({ adapter, level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const variant = variantForLevel(level);
    const handleTap = useCallback((idx) => adapter.handleCellTap(idx), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const litCells = useMemo(() => {
        if (adapterState.subPhase === 'pattern_show' && adapterState.stim) {
            return new Set(adapterState.stim.cells);
        }
        return new Set();
    }, [adapterState.subPhase, adapterState.stim]);
    const pickedCells = useMemo(() => new Set(adapterState.picked), [adapterState.picked]);
    const truthCells = useMemo(() => {
        return adapterState.stim ? new Set(adapterState.stim.cells) : new Set();
    }, [adapterState.stim]);
    const showFeedback = adapterState.subPhase === 'feedback';
    const k = adapterState.stim?.patternSize ?? 0;
    const tapsSoFar = adapterState.picked.length;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsxs("span", { className: "font-mono text-[11px]", children: [variant.gridSide, "x", variant.gridSide, " | k=", k] }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsxs("div", { className: "flex h-[36px] items-center justify-center text-[14px]", children: [adapterState.subPhase === 'fixation' && (_jsx("span", { className: "text-cyan-text-secondary", children: "\u2026" })), adapterState.subPhase === 'pattern_show' && (_jsx("span", { className: "font-display text-cyan-network", children: t('memory_matrix.phases.encoding') })), adapterState.subPhase === 'awaiting_input' && (_jsx("span", { className: "text-cyan-text-secondary", children: t('memory_matrix.phases.input', { taps: tapsSoFar, k }) })), adapterState.subPhase === 'feedback' && (_jsx("span", { className: [
                            'font-display text-[18px] font-medium',
                            adapterState.feedback === 'correct'
                                ? 'text-cyan-core'
                                : 'text-cyan-media',
                        ].join(' '), children: adapterState.feedback === 'correct'
                            ? `✓ ${t('memory_matrix.feedback.correct')}`
                            : `✗ ${t('memory_matrix.feedback.incorrect')}` }))] }), _jsx(MatrixGrid, { side: variant.gridSide, litCells: litCells, pickedCells: pickedCells, truthCells: truthCells, feedback: adapterState.feedback, showFeedback: showFeedback, onTap: handleTap, disabled: adapterState.subPhase !== 'awaiting_input' }), _jsx("div", { className: "text-[11px] text-cyan-text-secondary", children: t('memory_matrix.footer_hint') })] }));
}
function recommendKey(threshold) {
    if (threshold !== null && threshold >= 6) {
        return 'memory_matrix.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold >= 4) {
        return 'memory_matrix.debrief.recommend_good';
    }
    return 'memory_matrix.debrief.recommend_keep';
}
export function MMDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const bestK = useMemo(() => {
        let best = 0;
        for (const tt of staircase.trialLog) {
            if (tt.response.correct && tt.value > best)
                best = tt.value;
        }
        return Math.round(best);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : threshold.toFixed(1).replace(/\.0$/, '');
    const replay = () => {
        window.location.reload();
    };
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('memory_matrix.debrief.title'), scoreLabel: t('memory_matrix.debrief.score_label'), scoreValue: thresholdLabel, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            { label: t('memory_matrix.debrief.metric_best_k'), value: bestK },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('memory_matrix.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
//# sourceMappingURL=staircaseView.js.map